import { useEffect, useState } from 'react';
import { DeepMap, FieldError, useController, useFormContext } from 'react-hook-form';

import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';
import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import { getErrorForArrayField } from 'adapter/in/common/hooks/useArrayField';
import { useDebounce } from 'adapter/in/dashboards/utils/useDebounce';
import { ConfigFieldType, Fields, FieldStatus, FormFieldData, RulesRHF, TextInputFieldData } from 'adapter/in/common/form/types';

const DEBOUNCE_TIMEOUT = 400;

type UseNumberContainerProps = {
    value: string;
    error: string | undefined;
    handleOnChange: (value: string) => void;
    invalid: boolean;
};

type TextError = {
    message: string;
};

const getDisplayedValue = (value: TextInputFieldData) =>
    value?.status === FieldStatus.INITIAL_VALUE ? value.getInitialValue() : value?.value ?? '';

// NUMERIC fields are optional: unlike TEXT, an empty value must still be considered HAS_VALUE so it never blocks form submission
const useNumberContainer = (
    name: string,
    configType: ConfigFieldType,
    rules: RulesRHF = {
        required: false,
        validate: () => true,
    },
    dataDependsOn?: string[],
    defaultValue?: TextInputFieldData
): UseNumberContainerProps => {
    const { control, setValue, clearErrors } = useFormContext<Fields<TextInputFieldData>>();
    const {
        field,
        fieldState: { invalid },
        formState,
    } = useController({
        name,
        rules,
        control,
        defaultValue: defaultValue || buildTextInputFieldData(FieldStatus.HAS_VALUE, '', ConfigFieldType.OPTIONS, ''),
    });
    const { errors } = formState;
    const { setHasChange: setWidgetMenuHasChange } = dispatchFormChangesView();
    const [error, setError] = useState<string>();
    const fieldValue = field.value as TextInputFieldData;

    useEffect(() => {
        if (name.includes('.')) {
            const explodeName = name.split('.');
            const errorArray = getErrorForArrayField(
                errors as DeepMap<Fields<FormFieldData>, FieldError>,
                explodeName[0],
                Number(explodeName[1]),
                explodeName[2]
            );
            setError(errorArray);
        } else {
            setError(((errors[name] as unknown) as TextError)?.message);
        }
    }, [formState]);

    const [localValue, setLocalValue] = useState<string>(getDisplayedValue(fieldValue));
    const debouncedValue = useDebounce(localValue, DEBOUNCE_TIMEOUT);

    // Keep the local (immediate) value in sync when the field is reset/loaded from outside (e.g. clear, dependencies changing)
    useEffect(() => {
        const displayedValue = getDisplayedValue(fieldValue);
        if (displayedValue !== localValue) {
            setLocalValue(displayedValue);
        }
    }, [fieldValue?.status, fieldValue?.value]);

    // Propagate the debounced value to the form once the user stops typing. Status stays HAS_VALUE even when empty.
    useEffect(() => {
        if (debouncedValue !== getDisplayedValue(fieldValue)) {
            setWidgetMenuHasChange(true);
            clearErrors();
            setValue(name, buildTextInputFieldData(FieldStatus.HAS_VALUE, fieldValue.getInitialValue(), configType, debouncedValue));
        }
    }, [debouncedValue]);

    return {
        value: localValue,
        error,
        invalid,
        handleOnChange: setLocalValue,
    };
};

export default useNumberContainer;
